import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Input from '../../generic/Input'
import Button from '../../generic/Button'
import { get } from '../../data/api'
import CircularProgress from '@material-ui/core/CircularProgress'
import './SeshatLookup.css'

const TABS = {
	DOMAIN_SEARCH: 'domain-search',
	MATCH_CHECK: 'match-check',
	CUSTOMERS: 'customers',
}

const VALID_TABS = Object.values(TABS)

class SeshatLookup extends Component {
	constructor(props, context) {
		super(props, context)

		const urlParams = new URLSearchParams(window.location.search)
		const tabParam = urlParams.get('tab')
		const initialTab =
			tabParam && VALID_TABS.includes(tabParam) ? tabParam : TABS.DOMAIN_SEARCH

		this.state = {
			activeTab: initialTab,

			// UC1: domain search
			domainInput: '',
			domainCustomerName: '',
			domainResults: null,
			domainLoading: false,
			domainError: null,

			// UC2: infringement match
			matchCustomerName: '',
			matchClientImageId: '',
			matchPageUrl: '',
			matchResult: null,
			matchLoading: false,
			matchError: null,

			// UC3: customers list
			customers: null,
			customersLoading: false,
			customersError: null,
			customersFilter: '',
		}
	}

	componentDidMount() {
		if (this.state.activeTab === TABS.CUSTOMERS) {
			this.loadCustomers()
		}
	}

	handleTabChange = (tab) => {
		this.setState({ activeTab: tab })
		const urlParams = new URLSearchParams(window.location.search)
		urlParams.set('tab', tab)
		const newUrl = `${window.location.pathname}?${urlParams.toString()}`
		window.history.replaceState(null, '', newUrl)
		if (
			tab === TABS.CUSTOMERS &&
			this.state.customers === null &&
			!this.state.customersLoading
		) {
			this.loadCustomers()
		}
	}

	loadCustomers = async () => {
		this.setState({ customersLoading: true, customersError: null })
		try {
			const result = await get('/seshat/customers')
			this.setState({
				customers: result.customers || [],
				customersLoading: false,
			})
		} catch (err) {
			this.setState({
				customersError: err.message || 'Failed to load customers',
				customersLoading: false,
			})
		}
	}

	searchDomain = async () => {
		const domain = this.state.domainInput.trim()
		if (!domain) return
		const customerName = this.state.domainCustomerName.trim()
		this.setState({
			domainLoading: true,
			domainResults: null,
			domainError: null,
		})
		const params = new URLSearchParams({ domain })
		if (customerName) {
			params.set('customerName', customerName)
		}
		try {
			const result = await get(`/seshat/cases?${params.toString()}`)
			this.setState({ domainResults: result, domainLoading: false })
		} catch (err) {
			this.setState({
				domainError: err.message || 'Lookup failed',
				domainLoading: false,
			})
		}
	}

	checkMatch = async () => {
		const { matchClientImageId, matchCustomerName, matchPageUrl } = this.state
		const hasImagePair =
			matchClientImageId.trim() && matchCustomerName.trim()
		const hasUrl = matchPageUrl.trim()
		if (!hasImagePair && !hasUrl) return
		this.setState({
			matchLoading: true,
			matchResult: null,
			matchError: null,
		})
		const params = new URLSearchParams()
		if (hasImagePair) {
			params.set('clientImageId', matchClientImageId.trim())
			params.set('customerName', matchCustomerName.trim())
		}
		if (hasUrl) {
			params.set('pageUrl', matchPageUrl.trim())
		}
		try {
			const result = await get(`/seshat/match?${params.toString()}`)
			this.setState({ matchResult: result, matchLoading: false })
		} catch (err) {
			this.setState({
				matchError: err.message || 'Lookup failed',
				matchLoading: false,
			})
		}
	}

	render() {
		const { activeTab } = this.state

		return (
			<div className='seshat-container'>
				<div className='seshat-header'>
					<h1 className='seshat-title'>Seshat Lookup</h1>
					<p className='seshat-subtitle'>
						Read-only archive of cases and customer-domain notes from the
						decommissioned Seshat platform.
					</p>
				</div>

				<div className='seshat-tabs'>
					<button
						className={`tab-button ${activeTab === TABS.DOMAIN_SEARCH ? 'active' : ''}`}
						onClick={() => this.handleTabChange(TABS.DOMAIN_SEARCH)}
					>
						Search by webdomain
					</button>
					<button
						className={`tab-button ${activeTab === TABS.MATCH_CHECK ? 'active' : ''}`}
						onClick={() => this.handleTabChange(TABS.MATCH_CHECK)}
					>
						Match against existing case
					</button>
					<button
						className={`tab-button ${activeTab === TABS.CUSTOMERS ? 'active' : ''}`}
						onClick={() => this.handleTabChange(TABS.CUSTOMERS)}
					>
						Customers
					</button>
				</div>

				<div className='seshat-tab-content'>
					{activeTab === TABS.DOMAIN_SEARCH && (
						<DomainSearchSection
							inputValue={this.state.domainInput}
							customerName={this.state.domainCustomerName}
							onInputChange={(e) =>
								this.setState({ domainInput: e.target.value })
							}
							onCustomerNameChange={(e) =>
								this.setState({ domainCustomerName: e.target.value })
							}
							onSubmit={this.searchDomain}
							loading={this.state.domainLoading}
							results={this.state.domainResults}
							error={this.state.domainError}
						/>
					)}
					{activeTab === TABS.MATCH_CHECK && (
						<MatchCheckSection
							customerName={this.state.matchCustomerName}
							clientImageId={this.state.matchClientImageId}
							pageUrl={this.state.matchPageUrl}
							onCustomerNameChange={(e) =>
								this.setState({ matchCustomerName: e.target.value })
							}
							onClientImageIdChange={(e) =>
								this.setState({ matchClientImageId: e.target.value })
							}
							onPageUrlChange={(e) =>
								this.setState({ matchPageUrl: e.target.value })
							}
							onSubmit={this.checkMatch}
							loading={this.state.matchLoading}
							result={this.state.matchResult}
							error={this.state.matchError}
						/>
					)}
					{activeTab === TABS.CUSTOMERS && (
						<CustomersSection
							customers={this.state.customers}
							loading={this.state.customersLoading}
							error={this.state.customersError}
							filter={this.state.customersFilter}
							onFilterChange={(e) =>
								this.setState({ customersFilter: e.target.value })
							}
							onReload={this.loadCustomers}
						/>
					)}
				</div>
			</div>
		)
	}
}

const DomainSearchSection = ({
	inputValue,
	customerName,
	onInputChange,
	onCustomerNameChange,
	onSubmit,
	loading,
	results,
	error,
}) => (
	<section>
		<p style={{ color: '#666', marginTop: 0 }}>
			Find cases on a domain and any notes attached to a customer for that
			domain. Pasting a full URL is fine — protocol and <code>www.</code> are
			stripped automatically. Add a customer name to scope to a single
			customer.
		</p>
		<div
			style={{
				display: 'flex',
				gap: '10px',
				alignItems: 'flex-end',
				flexWrap: 'wrap',
			}}
		>
			<div style={{ flex: '1 1 260px', maxWidth: '420px' }}>
				<Input
					placeholder='Domain or URL (e.g. example.com)'
					value={inputValue}
					onChange={onInputChange}
					onKeyDown={(e) => {
						if (e.key === 'Enter') onSubmit()
					}}
				/>
			</div>
			<div style={{ flex: '1 1 260px', maxWidth: '420px' }}>
				<Input
					placeholder='Customer name (optional)'
					value={customerName}
					onChange={onCustomerNameChange}
					onKeyDown={(e) => {
						if (e.key === 'Enter') onSubmit()
					}}
				/>
			</div>
			<Button onClick={onSubmit} disabled={loading || !inputValue.trim()}>
				Search
			</Button>
		</div>

		{loading && (
			<div style={{ marginTop: '20px' }}>
				<CircularProgress size={24} />
			</div>
		)}
		{error && (
			<div style={{ color: 'red', marginTop: '15px' }}>{error}</div>
		)}
		{results && !loading && <DomainResults results={results} />}
	</section>
)

const DomainResults = ({ results }) => {
	const {
		domain,
		customer_name: customerName,
		infringements,
		infringements_total: infringementsTotal,
		notes,
		notes_total: notesTotal,
	} = results
	const noData = infringements.length === 0 && notes.length === 0
	const infringementsTruncated =
		typeof infringementsTotal === 'number' &&
		infringements.length < infringementsTotal
	const notesTruncated =
		typeof notesTotal === 'number' && notes.length < notesTotal

	return (
		<div style={{ marginTop: '25px' }}>
			<h3 style={{ marginBottom: '6px' }}>
				Results for <code>{domain}</code>
				{customerName && (
					<>
						{' '}
						<span style={{ fontSize: '0.85em', color: '#666' }}>
							scoped to <code>{customerName}</code>
						</span>
					</>
				)}
			</h3>
			{noData && (
				<p style={{ color: '#888' }}>
					No cases or notes were recorded for this domain
					{customerName ? ` and customer "${customerName}"` : ''} in Seshat.
				</p>
			)}

			{infringements.length > 0 && (
				<div style={{ marginTop: '20px' }}>
					<h4>
						Cases (
						{typeof infringementsTotal === 'number' && infringementsTotal !== infringements.length
							? `${infringements.length} of ${infringementsTotal}`
							: infringements.length}
						)
					</h4>
					{infringementsTruncated && (
						<TruncationNotice
							shown={infringements.length}
							total={infringementsTotal}
							noun='cases'
						/>
					)}
					<table className='bordered' style={{ width: '100%', fontSize: '0.85em' }}>
						<thead>
							<tr>
								<th>Case</th>
								<th>Status</th>
								<th>Created</th>
								<th>Customer</th>
								<th>Client image</th>
								<th>Page URL</th>
								<th>Infringer</th>
							</tr>
						</thead>
						<tbody>
							{infringements.map((row, i) => (
								<tr key={`${row.case_number}-${i}`}>
									<td>{row.case_number}</td>
									<td>{row.case_status}</td>
									<td>{formatDate(row.case_created_date)}</td>
									<td title={row.customer_identification_number}>
										{row.customer_name}
									</td>
									<td title={row.client_image_filename}>
										{row.client_image_id}
									</td>
									<td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
										<a href={row.page_url} target='_blank' rel='noopener noreferrer'>
											{row.page_url}
										</a>
									</td>
									<td>
										{row.infringer_name}
										{row.infringer_country_code &&
											` (${row.infringer_country_code})`}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{notes.length > 0 && (
				<div style={{ marginTop: '30px' }}>
					<h4>
						Customer notes on this domain (
						{typeof notesTotal === 'number' && notesTotal !== notes.length
							? `${notes.length} of ${notesTotal}`
							: notes.length}
						)
					</h4>
					{notesTruncated && (
						<TruncationNotice
							shown={notes.length}
							total={notesTotal}
							noun='notes'
						/>
					)}
					{notes.map((note, i) => {
						const isDeleted = !!note.note_deleted_date
						return (
							<div
								key={i}
								style={{
									border: '1px solid #ddd',
									borderRadius: '4px',
									padding: '12px 14px',
									marginBottom: '10px',
									background: isDeleted
										? '#f4f4f4'
										: note.is_public
										? '#fffaf0'
										: '#f9f9f9',
									opacity: isDeleted ? 0.6 : 1,
								}}
							>
								<div
									style={{
										fontSize: '0.8em',
										color: '#666',
										marginBottom: '6px',
									}}
								>
									<strong>{note.customer_name}</strong>
									{note.customer_identification_number &&
										` (${note.customer_identification_number})`}
									{' · '}
									{note.note_author || 'unknown author'}
									{' · '}
									{formatDate(note.note_created_date)}
									{note.is_public && (
										<span style={{ color: '#b8860b', marginLeft: '8px' }}>
											public
										</span>
									)}
									{isDeleted && (
										<span
											style={{
												color: '#b00020',
												marginLeft: '8px',
												fontWeight: 'bold',
											}}
										>
											deleted{' '}
											{formatDate(note.note_deleted_date) &&
												`on ${formatDate(note.note_deleted_date)}`}
										</span>
									)}
								</div>
								<div
									style={{
										whiteSpace: 'pre-wrap',
										textDecoration: isDeleted ? 'line-through' : 'none',
										color: isDeleted ? '#666' : 'inherit',
									}}
								>
									{note.note_message}
								</div>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

const MatchCheckSection = ({
	customerName,
	clientImageId,
	pageUrl,
	onCustomerNameChange,
	onClientImageIdChange,
	onPageUrlChange,
	onSubmit,
	loading,
	result,
	error,
}) => {
	const hasImagePair = customerName.trim() && clientImageId.trim()
	const hasUrl = pageUrl.trim()
	const canSubmit = (hasImagePair || hasUrl) && !loading
	return (
		<section>
			<p style={{ color: '#666', marginTop: 0 }}>
				Check whether an infringement has already been picked up. Match is{' '}
				<strong>OR</strong> across two dimensions: the customer's image (provide
				both customer name and client image id) <em>or</em> the infringement page
				URL. Either is sufficient — fill in both for a stronger signal.
			</p>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: '8px',
					maxWidth: '600px',
				}}
			>
				<div style={{ fontSize: '0.85em', color: '#888', marginTop: '4px' }}>
					Match by customer's image (both fields required together)
				</div>
				<Input
					placeholder='Customer name'
					value={customerName}
					onChange={onCustomerNameChange}
				/>
				<Input
					placeholder='Client image id'
					value={clientImageId}
					onChange={onClientImageIdChange}
				/>
				<div style={{ fontSize: '0.85em', color: '#888', marginTop: '12px' }}>
					Or match by URL
				</div>
				<Input
					placeholder='Infringement page URL'
					value={pageUrl}
					onChange={onPageUrlChange}
				/>
				<div style={{ marginTop: '4px' }}>
					<Button onClick={onSubmit} disabled={!canSubmit}>
						Check match
					</Button>
				</div>
			</div>

			{loading && (
				<div style={{ marginTop: '20px' }}>
					<CircularProgress size={24} />
				</div>
			)}
			{error && <div style={{ color: 'red', marginTop: '15px' }}>{error}</div>}
			{result && !loading && <MatchResult result={result} />}
		</section>
	)
}

const MatchResult = ({ result }) => {
	const { match, cases } = result
	return (
		<div style={{ marginTop: '20px' }}>
			<div
				style={{
					padding: '12px 16px',
					borderRadius: '4px',
					background: match ? '#fff4d6' : '#e8f5e9',
					color: match ? '#8a6d00' : '#1b5e20',
					fontWeight: 'bold',
					marginBottom: '15px',
				}}
			>
				{match
					? `Match — already picked up in Seshat (${cases.length} case${cases.length === 1 ? '' : 's'})`
					: 'No match — not previously seen in Seshat'}
			</div>
			{match && (
				<table className='bordered' style={{ width: '100%', fontSize: '0.85em' }}>
					<thead>
						<tr>
							<th>Matched on</th>
							<th>Case</th>
							<th>Status</th>
							<th>Created</th>
							<th>Closed</th>
							<th>Domain</th>
							<th>Page URL</th>
							<th>Customer</th>
							<th>Client image</th>
						</tr>
					</thead>
					<tbody>
						{cases.map((row, i) => (
							<tr key={`${row.case_number}-${i}`}>
								<td>
									{row.matched_by_image && row.matched_by_url
										? 'image + url'
										: row.matched_by_image
										? 'image'
										: row.matched_by_url
										? 'url'
										: ''}
								</td>
								<td>{row.case_number}</td>
								<td>{row.case_status}</td>
								<td>{formatDate(row.case_created_date)}</td>
								<td>{formatDate(row.case_closing_date)}</td>
								<td>{row.registrable_domain}</td>
								<td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
									<a href={row.page_url} target='_blank' rel='noopener noreferrer'>
										{row.page_url}
									</a>
								</td>
								<td title={row.customer_identification_number}>
									{row.customer_name}
								</td>
								<td title={row.client_image_filename}>{row.client_image_id}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	)
}

const CustomersSection = ({
	customers,
	loading,
	error,
	filter,
	onFilterChange,
	onReload,
}) => {
	if (loading && customers === null) {
		return (
			<section>
				<p style={{ color: '#666', marginTop: 0 }}>
					Listing all customers that have at least one infringement or note in
					Seshat.
				</p>
				<CircularProgress size={24} />
			</section>
		)
	}
	if (error) {
		return (
			<section>
				<div style={{ color: 'red', marginBottom: '15px' }}>{error}</div>
				<Button onClick={onReload}>Retry</Button>
			</section>
		)
	}
	if (!customers) return null

	const filterLower = filter.trim().toLowerCase()
	const filtered = filterLower
		? customers.filter((c) => {
				const name = (c.customer_name || '').toLowerCase()
				const id = (c.customer_identification_number || '').toLowerCase()
				return name.includes(filterLower) || id.includes(filterLower)
			})
		: customers

	const ambiguousNames = countDuplicateNames(customers)

	return (
		<section>
			<p style={{ color: '#666', marginTop: 0 }}>
				All customers with any case or note in the Seshat archive (
				{customers.length.toLocaleString()} total
				{ambiguousNames > 0 &&
					`, ${ambiguousNames} customer name${
						ambiguousNames === 1 ? '' : 's'
					} shared by multiple legal entities`}
				).
			</p>
			<div style={{ maxWidth: '420px', marginBottom: '20px' }}>
				<Input
					placeholder='Filter by name or identification number'
					value={filter}
					onChange={onFilterChange}
				/>
			</div>
			{filtered.length === 0 ? (
				<p style={{ color: '#888' }}>No customers match this filter.</p>
			) : (
				<table className='bordered' style={{ width: '100%', fontSize: '0.9em' }}>
					<thead>
						<tr>
							<th>Customer</th>
							<th>Identification number</th>
							<th style={{ textAlign: 'right' }}>Cases</th>
							<th style={{ textAlign: 'right' }}>Infringements</th>
							<th style={{ textAlign: 'right' }}>Notes</th>
						</tr>
					</thead>
					<tbody>
						{filtered.map((c, i) => (
							<tr
								key={`${c.customer_name}-${c.customer_identification_number}-${i}`}
							>
								<td>{c.customer_name || <em>(no name)</em>}</td>
								<td>
									{c.customer_identification_number || (
										<em style={{ color: '#aaa' }}>—</em>
									)}
								</td>
								<td style={{ textAlign: 'right' }}>{c.case_count}</td>
								<td style={{ textAlign: 'right' }}>{c.infringement_count}</td>
								<td style={{ textAlign: 'right' }}>{c.note_count}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</section>
	)
}

const countDuplicateNames = (customers) => {
	const counts = {}
	customers.forEach((c) => {
		const key = (c.customer_name || '').toLowerCase()
		if (!key) return
		counts[key] = (counts[key] || 0) + 1
	})
	return Object.values(counts).filter((n) => n > 1).length
}

const formatDate = (value) => {
	if (!value) return ''
	const d = new Date(value)
	if (isNaN(d.getTime())) return value
	return d.toISOString().slice(0, 10)
}

const TruncationNotice = ({ shown, total, noun }) => (
	<div
		style={{
			padding: '8px 12px',
			marginBottom: '10px',
			background: '#fff8e1',
			border: '1px solid #ffe082',
			borderRadius: '4px',
			fontSize: '0.85em',
			color: '#7a5a00',
		}}
	>
		Showing the first {shown.toLocaleString()} of {total.toLocaleString()}{' '}
		{noun}. Refine your search if you need to see more.
	</div>
)

SeshatLookup.contextTypes = {
	gettext: PropTypes.func,
}

export default SeshatLookup
